import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid2 as Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { fetchOverview, type OverviewCampaign } from '../api/overview';
import { currentQuarter, quarterLabel } from '../api/okrs';
import { PageHeader } from '../components/PageHeader';
import { DataState } from '../components/DataState';
import { formatDate, formatDateTime, formatDelta, formatMetric, formatMonthLabel } from '../components/formatters';
import { OkrStatusChip, TASK_STATUS_META, TASK_STATUS_ORDER } from './okr/OkrShared';
import { useAuth } from '../auth/useAuth';

function clampPct(p: number | null): number {
  if (p == null || !Number.isFinite(p)) return 0;
  return Math.max(0, Math.min(100, p * 100));
}

function pctText(p: number | null): string {
  return p != null && Number.isFinite(p) ? `${Math.round(p * 100)}%` : '—';
}

function AdviceList({ title, items, color }: { title: string; items: string[]; color: string }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing to show.
        </Typography>
      ) : (
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
          {items.map((item, i) => (
            <Typography key={i} variant="body2" component="li">
              {item}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function CampaignCard({ campaign }: { campaign: OverviewCampaign }) {
  const pct = clampPct(campaign.progressPct);
  const reached = campaign.progressPct != null && campaign.progressPct >= 1;

  const status = !campaign.started
    ? { label: 'Upcoming', color: 'info' as const }
    : campaign.ended
      ? { label: 'Ended', color: 'default' as const }
      : { label: 'Live', color: 'success' as const };

  const timing = !campaign.started
    ? `Starts ${formatDate(campaign.startDate)}`
    : campaign.ended
      ? `Ended ${formatDate(campaign.endDate)}`
      : `Day ${campaign.daysElapsed} of ${campaign.daysTotal} · ${campaign.daysRemaining} ${campaign.daysRemaining === 1 ? 'day' : 'days'} left`;

  return (
    <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} gap={1}>
          <Typography variant="h6">{campaign.label}</Typography>
          <Chip size="small" color={status.color} label={status.label} variant={campaign.ended ? 'outlined' : 'filled'} />
        </Stack>

        <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {formatMetric(campaign.revenue, 'currency', { compact: true })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            of {formatMetric(campaign.target, 'currency', { compact: true })} target · {pctText(campaign.progressPct)}
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={pct}
          color={reached ? 'success' : 'primary'}
          sx={{ height: 10, borderRadius: 5, my: 1, bgcolor: 'grey.100' }}
        />

        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            {timing}
            {campaign.orders > 0 ? ` · ${formatMetric(campaign.orders, 'count')} orders` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Shopify PL, net of VAT · updated {formatDateTime(campaign.updatedAt)}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { user } = useAuth();

  const overviewQuery = useQuery({
    queryKey: ['overview'],
    queryFn: ({ signal }) => fetchOverview(false, signal),
  });

  const firstName = user?.name?.split(' ')[0] ?? 'there';

  return (
    <Box>
      <PageHeader title={`Welcome back, ${firstName}`} subtitle={`${quarterLabel(currentQuarter())} dashboard`} />

      <DataState loading={overviewQuery.isLoading} error={overviewQuery.error} data={overviewQuery.data}>
        {(data) => (
          <Stack spacing={4}>
            {data.campaign && <CampaignCard campaign={data.campaign} />}

            {data.headlineMetrics.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Your key metrics
                </Typography>
                <Grid container spacing={2}>
                  {data.headlineMetrics.map((m) => {
                    const delta = formatDelta(m.growthPct);
                    return (
                      <Grid key={`${m.layer}::${m.area}::${m.label}`} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Typography variant="caption" color="text.secondary" noWrap display="block" title={`${m.layer} · ${m.area}`}>
                              {m.label}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                              {m.latestActual != null ? formatMetric(m.latestActual, m.unit, { compact: true }) : '—'}
                            </Typography>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {m.latestMonth ? formatMonthLabel(m.latestMonth) : ''}
                                {m.mtd ? ' · MTD' : ''}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    delta.positive === null
                                      ? 'text.secondary'
                                      : delta.positive
                                        ? 'success.main'
                                        : 'error.main',
                                }}
                              >
                                {delta.label}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="h6">Your OKRs</Typography>
                      {data.okrSummary && <OkrStatusChip status={data.okrSummary.status} />}
                    </Stack>

                    {data.okrSummary ? (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography variant="body2" color="text.secondary">
                              Overall progress
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {pctText(data.okrSummary.overallProgressPct)}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={clampPct(data.okrSummary.overallProgressPct)}
                            sx={{ height: 8, borderRadius: 4, mt: 0.5, bgcolor: 'grey.100' }}
                          />
                        </Box>
                        <Stack spacing={1.5}>
                          {data.okrSummary.keyResults.map((kr) => (
                            <Box key={kr.label}>
                              <Stack direction="row" justifyContent="space-between" gap={1} alignItems="baseline">
                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                  {kr.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                                  {kr.currentValue != null
                                    ? formatMetric(kr.currentValue, kr.unit, { compact: true })
                                    : '—'}
                                  {' / '}
                                  {formatMetric(kr.targetValue, kr.unit, { compact: true })} · {pctText(kr.progressPct)}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={clampPct(kr.progressPct)}
                                color={kr.progressPct != null && kr.progressPct >= 1 ? 'success' : 'primary'}
                                sx={{ height: 6, borderRadius: 3, mt: 0.25, bgcolor: 'grey.100' }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      </>
                    ) : (
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          You haven&apos;t set OKRs for {quarterLabel(data.quarter)} yet.
                        </Typography>
                        <Button component={RouterLink} to="/okrs" variant="contained" size="small">
                          Create your OKRs
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                      Task status
                    </Typography>
                    {data.taskSummary && data.taskSummary.total > 0 ? (
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {TASK_STATUS_ORDER.filter((s) => (data.taskSummary!.counts[s] ?? 0) > 0).map((s) => (
                          <Chip
                            key={s}
                            size="small"
                            color={TASK_STATUS_META[s].color}
                            variant={s === 'not_started' ? 'outlined' : 'filled'}
                            label={`${TASK_STATUS_META[s].label}: ${data.taskSummary!.counts[s]}`}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No tasks tracked for this quarter yet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }} gap={1}>
                  <Box>
                    <Typography variant="h6">Overview & Suggestions</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated {formatDate(data.advice.generatedAt)} · {data.advice.source === 'llm' ? 'AI' : 'auto'}
                    </Typography>
                  </Box>
                </Stack>

                {data.advice.summary && (
                  <Typography variant="body2" sx={{ mb: 2.5 }}>
                    {data.advice.summary}
                  </Typography>
                )}

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <AdviceList title="What's going well" items={data.advice.whatsGoingWell} color="success.main" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <AdviceList title="What to improve" items={data.advice.needsImprovement} color="warning.main" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <AdviceList title="Hints for the coming week" items={data.advice.hintsForNextWeek} color="info.main" />
                  </Grid>
                </Grid>

                {data.advice.actionItems.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <AdviceList title="Recommended actions" items={data.advice.actionItems} color="primary.main" />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
      </DataState>
    </Box>
  );
}
